"use client";

import { c as _c } from "react/compiler-runtime";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTRPC } from "@/lib/trpc/client";
const PAGE_SIZE = 20;

/** Stamps unread items with the current time, mirroring what the server does on mark-read. */
function stampRead<T extends {
  readAt: unknown;
}>(items: T[]): T[] {
  const now = new Date();
  return items.map(item => item.readAt ? item : Object.assign({}, item, {
    readAt: now
  }));
}
type UseNotificationsDataOptions = {
  /** The current filter view */
  filter: "unread" | "read";
  /** Whether the popover is open (gates fetching) */
  enabled: boolean;
};

/**
 * Hook for notifications data fetching and mutations.
 * Encapsulates all TanStack Query logic for the notifications popover.
 */
export function useNotificationsData(t0) {
  const $ = _c(36);
  const {
    filter,
    enabled
  } = t0;
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const inboxListQueryKey = trpc.notifications.list.infiniteQueryOptions({
    limit: PAGE_SIZE,
    filter: "unread"
  }, {
    getNextPageParam: _temp
  }).queryKey;
  const archiveListQueryKey = trpc.notifications.list.infiniteQueryOptions({
    limit: PAGE_SIZE,
    filter: "read"
  }, {
    getNextPageParam: _temp2
  }).queryKey;
  const countQueryKey = trpc.notifications.unreadCount.queryKey();
  let t2;
  if ($[0] !== queryClient || $[1] !== trpc.notifications.list || $[2] !== trpc.notifications.unreadCount) {
    t2 = () => {
      queryClient.invalidateQueries(trpc.notifications.list.queryFilter());
      queryClient.invalidateQueries(trpc.notifications.unreadCount.queryFilter());
    };
    $[0] = queryClient;
    $[1] = trpc.notifications.list;
    $[2] = trpc.notifications.unreadCount;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const markRead = useMutation(trpc.notifications.markRead.mutationOptions({
    onMutate: async t1 => {
      const {
        id
      } = t1;
      await queryClient.cancelQueries({
        queryKey: inboxListQueryKey
      });
      await queryClient.cancelQueries({
        queryKey: archiveListQueryKey
      });
      await queryClient.cancelQueries({
        queryKey: countQueryKey
      });
      const previousCount = queryClient.getQueryData(countQueryKey);
      const previousInbox = queryClient.getQueryData(inboxListQueryKey);
      const previousArchive = queryClient.getQueryData(archiveListQueryKey);
      const moved = previousInbox?.pages?.flatMap(_temp3).find(n => n.id === id);
      if (moved) {
        queryClient.setQueryData(countQueryKey, _temp4);
      }
      queryClient.setQueryData(inboxListQueryKey, old_0 => {
        if (!old_0?.pages) {
          return old_0;
        }
        return {
          ...old_0,
          pages: old_0.pages.map(page_0 => ({
            ...page_0,
            items: page_0.items.filter(n_0 => n_0.id !== id)
          }))
        };
      });
      const archivedItem = moved ? stampRead([moved])[0] : undefined;
      queryClient.setQueryData(archiveListQueryKey, old_1 => {
        if (!archivedItem) {
          return old_1;
        }
        if (!old_1?.pages?.length) {
          return {
            pages: [{
              items: [archivedItem],
              nextCursor: undefined
            }],
            pageParams: [null]
          };
        }
        const [first, ...rest] = old_1.pages;
        return {
          ...old_1,
          pages: [{
            ...first,
            items: [archivedItem, ...first.items]
          }, ...rest]
        };
      });
      return {
        previousCount,
        previousInbox,
        previousArchive
      };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousCount !== undefined) {
        queryClient.setQueryData(countQueryKey, context.previousCount);
      }
      if (context?.previousInbox) {
        queryClient.setQueryData(inboxListQueryKey, context.previousInbox);
      }
      if (context?.previousArchive) {
        queryClient.setQueryData(archiveListQueryKey, context.previousArchive);
      }
      toast.error("Failed to mark notification as read");
    },
    onSettled: t2
  }));
  let t3;
  if ($[4] !== queryClient || $[5] !== trpc.notifications.list || $[6] !== trpc.notifications.unreadCount) {
    t3 = () => {
      queryClient.invalidateQueries(trpc.notifications.list.queryFilter());
      queryClient.invalidateQueries(trpc.notifications.unreadCount.queryFilter());
    };
    $[4] = queryClient;
    $[5] = trpc.notifications.list;
    $[6] = trpc.notifications.unreadCount;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const markAllRead = useMutation(trpc.notifications.markAllRead.mutationOptions({
    onMutate: async () => {
      await queryClient.cancelQueries({
        queryKey: inboxListQueryKey
      });
      await queryClient.cancelQueries({
        queryKey: archiveListQueryKey
      });
      await queryClient.cancelQueries({
        queryKey: countQueryKey
      });
      const previousCount_0 = queryClient.getQueryData(countQueryKey);
      const previousInbox_0 = queryClient.getQueryData(inboxListQueryKey);
      const previousArchive_0 = queryClient.getQueryData(archiveListQueryKey);
      queryClient.setQueryData(countQueryKey, 0);
      const moved_0 = stampRead(previousInbox_0?.pages?.flatMap(_temp5) ?? []);
      queryClient.setQueryData(inboxListQueryKey, _temp7);
      queryClient.setQueryData(archiveListQueryKey, old_3 => {
        if (moved_0.length === 0) {
          return old_3;
        }
        if (!old_3?.pages?.length) {
          return {
            pages: [{
              items: moved_0,
              nextCursor: undefined
            }],
            pageParams: [null]
          };
        }
        const [first_0, ...rest_0] = old_3.pages;
        return {
          ...old_3,
          pages: [{
            ...first_0,
            items: [...moved_0, ...first_0.items]
          }, ...rest_0]
        };
      });
      return {
        previousCount: previousCount_0,
        previousInbox: previousInbox_0,
        previousArchive: previousArchive_0
      };
    },
    onError: (_err_0, _vars_0, context_0) => {
      if (context_0?.previousCount !== undefined) {
        queryClient.setQueryData(countQueryKey, context_0.previousCount);
      }
      if (context_0?.previousInbox) {
        queryClient.setQueryData(inboxListQueryKey, context_0.previousInbox);
      }
      if (context_0?.previousArchive) {
        queryClient.setQueryData(archiveListQueryKey, context_0.previousArchive);
      }
      toast.error("Failed to mark notifications as read");
    },
    onSettled: t3
  }));
  let t4;
  if ($[8] !== trpc.notifications.unreadCount) {
    t4 = trpc.notifications.unreadCount.queryOptions();
    $[8] = trpc.notifications.unreadCount;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t4) {
    t5 = {
      ...t4,
      refetchOnWindowFocus: true,
      refetchInterval: 60000
    };
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const {
    data: t6
  } = useQuery(t5);
  const count = t6 === undefined ? 0 : t6;
  let t7;
  if ($[12] !== enabled || $[13] !== filter || $[14] !== trpc.notifications.list) {
    t7 = trpc.notifications.list.infiniteQueryOptions({
      limit: PAGE_SIZE,
      filter
    }, {
      getNextPageParam: _temp8,
      refetchOnWindowFocus: true,
      staleTime: 0,
      enabled
    });
    $[12] = enabled;
    $[13] = filter;
    $[14] = trpc.notifications.list;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t7) {
    t8 = {
      ...t7
    };
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isFetchNextPageError,
    isLoading,
    isError
  } = useInfiniteQuery(t8);
  let t9;
  if ($[18] !== data?.pages) {
    t9 = data?.pages.flatMap(_temp9) ?? [];
    $[18] = data?.pages;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const notifications = t9;
  const showLoading = isLoading || isFetching && data === undefined;
  let t10;
  if ($[20] !== count || $[21] !== countQueryKey || $[22] !== queryClient) {
    t10 = () => queryClient.getQueryData(countQueryKey) ?? count;
    $[20] = count;
    $[21] = countQueryKey;
    $[22] = queryClient;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const getLatestUnreadCount = t10;
  let t11;
  if ($[24] !== count || $[25] !== fetchNextPage || $[26] !== getLatestUnreadCount || $[27] !== hasNextPage || $[28] !== isError || $[29] !== isFetchNextPageError || $[30] !== isFetchingNextPage || $[31] !== markAllRead || $[32] !== markRead || $[33] !== notifications || $[34] !== showLoading) {
    t11 = {
      notifications,
      count,
      showLoading,
      hasNextPage,
      isFetchingNextPage,
      isFetchNextPageError,
      isError,
      markRead,
      markAllRead,
      fetchNextPage,
      getLatestUnreadCount
    };
    $[24] = count;
    $[25] = fetchNextPage;
    $[26] = getLatestUnreadCount;
    $[27] = hasNextPage;
    $[28] = isError;
    $[29] = isFetchNextPageError;
    $[30] = isFetchingNextPage;
    $[31] = markAllRead;
    $[32] = markRead;
    $[33] = notifications;
    $[34] = showLoading;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
}
function _temp9(page_3) {
  return page_3.items;
}
function _temp8(lastPage_1) {
  return lastPage_1.nextCursor;
}
function _temp7(old_2) {
  if (!old_2?.pages) {
    return old_2;
  }
  return {
    ...old_2,
    pages: old_2.pages.map(_temp6)
  };
}
function _temp6(page_2) {
  return {
    ...page_2,
    nextCursor: undefined,
    items: []
  };
}
function _temp5(page_1) {
  return page_1.items;
}
function _temp4(old) {
  return typeof old === "number" ? Math.max(0, old - 1) : old;
}
function _temp3(page) {
  return page.items;
}
function _temp2(lastPage_0) {
  return lastPage_0.nextCursor;
}
function _temp(lastPage) {
  return lastPage.nextCursor;
}